import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Send, Paperclip, Plus, ArrowLeft, Users, FileText, X, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api, { fmtQual } from "../lib/api";
import { apriFile } from "../components/FileViewer";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const CH_LABELS = { amministrazione: "Amministrazione", responsabile: "Responsabile", coordinatore: "Coordinatore", preposto: "Preposto" };

// Immagine allegato: scarica il blob con autenticazione e la mostra inline
const ChatImage = ({ msgId, idx, op, filename }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let alive = true;
    api.get(`/chat/attachment/${msgId}/${idx}`, { params: op, responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [msgId, idx]); // eslint-disable-line
  if (!src) return <div className="w-40 h-28 rounded-lg bg-black/10 animate-pulse" />;
  return <img src={src} alt={filename} className="max-w-[220px] max-h-[220px] rounded-lg cursor-pointer" onClick={() => window.open(src, "_blank", "noopener")} />;
};

// Avatar operatore: prova a caricare la foto, fallback alle iniziali (stile WhatsApp)
const ChatAvatar = ({ opId, name, size = 40, bg = "#1f7a5c" }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let alive = true;
    if (!opId) return;
    api.get(`/firma/mio-account/foto`, { params: { op_id: opId }, responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [opId]);
  const st = { width: size, height: size };
  if (src) return <img src={src} alt={name} className="rounded-full object-cover shrink-0" style={st} />;
  return <span className="rounded-full grid place-items-center text-white font-bold text-sm shrink-0" style={{ ...st, background: bg }}>{initials(name)}</span>;
};

export default function Comunicazioni({ embedded = false }) {
  const { selectedStructure, structures = [] } = useAuth();
  const { identity, activeOperatore } = useFirma();
  const qc = useQueryClient();
  const op = useMemo(() => (identity?.operatore_id ? { op_id: identity.operatore_id, op_name: identity.operatore_nome } : {}), [identity]);
  const sid = activeOperatore?.structure_id || (selectedStructure && selectedStructure !== "all" ? selectedStructure : structures?.[0]?.id);

  const [active, setActive] = useState(null); // {key, mode, channel, token, name, title, subtitle, structure_id}
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [dirOpen, setDirOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef();
  const endRef = useRef();

  const { data: threads = [] } = useQuery({
    queryKey: ["chat-threads", identity?.operatore_id],
    refetchInterval: 6000,
    queryFn: async () => (await api.get("/chat/threads", { params: op })).data,
  });

  const { data: messages = [] } = useQuery({
    queryKey: ["chat-msgs", active?.key, identity?.operatore_id],
    enabled: !!active?.key,
    refetchInterval: 5000,
    queryFn: async () => (await api.get("/chat/messages", { params: { key: active.key, ...op } })).data,
  });

  const { data: directory } = useQuery({
    queryKey: ["chat-directory", sid, identity?.operatore_id],
    enabled: !!sid,
    refetchInterval: 15000,
    queryFn: async () => (await api.get("/chat/directory", { params: { structure_id: sid, ...op } })).data,
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, active?.key]);
  useEffect(() => { qc.invalidateQueries({ queryKey: ["chat-unread"] }); }, [messages, qc]);

  const openThread = (t) => setActive({ key: t.key, mode: "thread", title: t.title, subtitle: t.subtitle, structure_id: t.structure_id });
  const openDm = (o) => {
    if (o.member) setActive({ key: o.key, mode: "new_channel_member", channel: directory?.my_channel, token: o.token, name: o.name, title: o.name, subtitle: `via ${CH_LABELS[directory?.my_channel] || "Canale"}`, structure_id: sid });
    else setActive({ key: o.key, mode: "new_dm", token: o.token, name: o.name, title: o.name, subtitle: "Messaggio diretto", structure_id: sid });
    setDirOpen(false);
  };

  const send = async () => {
    if (!active) return;
    if (!text.trim() && files.length === 0) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("structure_id", active.structure_id || sid);
      if (text.trim()) fd.append("testo", text.trim());
      if (op.op_id) { fd.append("op_id", op.op_id); fd.append("op_name", op.op_name || ""); }
      if (active.mode === "new_channel") fd.append("new_channel", active.channel);
      else if (active.mode === "new_channel_member") { fd.append("channel", active.channel); fd.append("member_token", active.token); fd.append("member_name", active.name || ""); }
      else if (active.mode === "new_dm") { fd.append("to_token", active.token); fd.append("to_name", active.name || ""); }
      else fd.append("key", active.key);
      files.forEach((f) => fd.append("files", f));
      await api.post("/chat/send", fd);
      setText(""); setFiles([]); if (fileRef.current) fileRef.current.value = "";
      // dopo il primo invio la conversazione esiste: passo a modalità thread
      setActive((a) => ({ ...a, mode: "thread" }));
      qc.invalidateQueries({ queryKey: ["chat-msgs", active.key] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };

  const openAttach = (m, a) => apriFile(`/chat/attachment/${m.id}/${a.idx}`, a.filename || "allegato", op);

  return (
    <div data-testid="comunicazioni-page">
      {!embedded && (
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={22} className="text-primary" />
          <h1 className="text-2xl font-head font-bold">Comunicazioni</h1>
        </div>
      )}

      <div className={`gr-card overflow-hidden grid md:grid-cols-[320px_1fr] ${embedded ? "h-[560px]" : "h-[calc(100vh-190px)]"} min-h-[480px]`}>
        {/* Lista conversazioni */}
        <div className={`border-r flex-col min-h-0 ${active ? "hidden md:flex" : "flex"}`}>
          <div className="p-3 border-b">
            <Button className="w-full font-semibold" onClick={() => setDirOpen(true)} data-testid="chat-new-btn" disabled={!sid}><Plus size={16} className="mr-1" /> Nuova chat</Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {threads.map((t, i) => {
              const hasUnread = t.unread > 0;
              return (
              <button key={t.key} onClick={() => openThread(t)} data-testid={`chat-thread-${i}`}
                className={`w-full text-left px-3 py-2.5 border-b flex items-center gap-3 transition-colors ${active?.key === t.key ? "bg-secondary" : hasUnread ? "bg-emerald-50 hover:bg-emerald-100" : "hover:bg-secondary/50"}`}>
                <span className="relative shrink-0">
                  <span className="h-10 w-10 rounded-full grid place-items-center text-white font-bold text-sm" style={{ background: t.channel ? "#2f6fb0" : "#1f7a5c" }}>{initials(t.title)}</span>
                  {hasUnread && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" data-testid={`chat-dot-${i}`} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className={`text-sm truncate flex-1 ${hasUnread ? "font-bold text-foreground" : "font-semibold"}`}>{t.title}</span>
                    <span className={`text-[10px] shrink-0 ${hasUnread ? "text-emerald-600 font-bold" : "text-muted-foreground"}`}>{timeOf(t.last_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs truncate flex-1 ${hasUnread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{t.last_text || t.subtitle}</span>
                    {hasUnread && <span data-testid={`chat-unread-${i}`} className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold grid place-items-center">{t.unread > 99 ? "99+" : t.unread}</span>}
                  </div>
                </div>
              </button>
              );
            })}

            <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground bg-secondary/40 border-b flex items-center gap-1.5"><Users size={13} /> Operatori struttura</div>
            {(directory?.operatori || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-6 px-4">Nessun altro operatore attivo in struttura.</p>}
            {(directory?.operatori || []).map((o, i) => (
              <button key={o.token} onClick={() => openDm(o)} data-testid={`chat-op-${i}`}
                className={`w-full text-left px-3 py-2.5 border-b flex items-center gap-3 transition-colors ${active?.key === o.key ? "bg-secondary" : "hover:bg-secondary/50"}`}>
                <span className="relative shrink-0">
                  <ChatAvatar opId={o.op_id} name={o.name} />
                  {o.online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" title="Firma attiva" data-testid={`chat-online-${i}`} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate flex items-center gap-1.5">{o.name}{o.online && <span className="text-[10px] font-bold text-emerald-600">online</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{fmtQual(o.ruolo) || fmtQual(o.qualifica) || "Operatore"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversazione attiva */}
        <div className={`flex-col min-h-0 ${active ? "flex" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 grid place-items-center text-muted-foreground text-sm p-6 text-center">
              <div><MessageSquare size={40} className="mx-auto mb-3 opacity-30" />Seleziona una conversazione o avvia una nuova chat</div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b flex items-center gap-3">
                <button className="md:hidden" onClick={() => setActive(null)}><ArrowLeft size={18} /></button>
                <span className="h-9 w-9 rounded-full grid place-items-center text-white font-bold text-sm" style={{ background: active.channel ? "#2f6fb0" : "#1f7a5c" }}>{initials(active.title)}</span>
                <div><div className="font-bold text-sm leading-tight" data-testid="chat-active-title">{active.title}</div><div className="text-[11px] text-muted-foreground">{active.subtitle}</div></div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 bg-secondary/20" data-testid="chat-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${m.mine ? "bg-primary text-white rounded-br-sm" : "bg-white rounded-bl-sm"}`}>
                      {!m.mine && <div className="text-[11px] font-bold mb-0.5 text-primary">{m.from_name}</div>}
                      {(m.attachments || []).map((a) => (
                        <div key={a.idx} className="mb-1">
                          {a.is_image
                            ? <ChatImage msgId={m.id} idx={a.idx} op={op} filename={a.filename} />
                            : <button onClick={() => openAttach(m, a)} className={`flex items-center gap-1.5 text-xs font-semibold underline ${m.mine ? "text-white" : "text-primary"}`}><FileText size={13} /> {a.filename}</button>}
                        </div>
                      ))}
                      {m.testo && <div className="text-sm whitespace-pre-wrap break-words">{m.testo}</div>}
                      <div className={`text-[10px] mt-0.5 flex items-center gap-1 justify-end ${m.mine ? "text-white/70" : "text-muted-foreground"}`}>
                        {timeOf(m.created_at)}
                        {m.mine && (m.read ? <CheckCheck size={13} className="text-sky-300" data-testid="chat-read" /> : <Check size={13} className="text-white/60" data-testid="chat-sent" />)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              <div className="border-t p-3">
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {files.map((f, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs bg-secondary rounded-full px-2 py-1">
                        <Paperclip size={11} /> {f.name}
                        <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input type="file" ref={fileRef} className="hidden" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" data-testid="chat-attach"
                    onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                  <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn"><Paperclip size={18} /></Button>
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio..." data-testid="chat-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                  <Button onClick={send} disabled={sending} data-testid="chat-send" className="font-semibold"><Send size={16} /></Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={dirOpen} onOpenChange={setDirOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuova chat</DialogTitle>
            <DialogDescription>Scegli un canale o un collega con cui parlare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1"><Users size={13} /> Operatori</div>
              <div className="space-y-1.5">
                {(directory?.operatori || []).length === 0 && <p className="text-xs text-muted-foreground">Nessun altro operatore nella struttura.</p>}
                {(directory?.operatori || []).map((o, i) => (
                  <button key={o.token} onClick={() => openDm(o)} data-testid={`chat-dir-op-${i}`}
                    className="w-full text-left px-3 py-2 rounded-lg border hover:border-primary transition-colors flex items-center gap-2 text-sm">
                    <span className="h-8 w-8 rounded-full grid place-items-center text-white text-xs" style={{ background: "#1f7a5c" }}>{initials(o.name)}</span>
                    <span className="font-semibold flex-1">{o.name}</span>
                    <span className="text-[11px] text-muted-foreground">{fmtQual(o.qualifica)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
